// chatbot logic (ported from public/static/index.html)
// supports three modes: claude dev server, openai (gpt-4o), and mock

import { getDashboardRawData, dashboardFmtDate, dashboardParseDate } from '../Utilities'
import de from '../../locales/de.json'

// api key (obfuscated, same as original)
const _obf = 'QXNUR05PZEFoR0RlZGNQX1NuNlNHYkpqTWJLOGlkWUllV18xZldYZXRGakZGM195QkpmQksxZTAwYk02VjNsRGF1dmtMaDFlbmNKRmtibEIzVHdRa1ZyV2pXUW1Banc4blppMUR6SWVELUpqNGJoUksyTHhwR3VtVE8xT3o5Q24wS240eDVRWWc0alVPVE9NcHRPWWVuUnR1WjdrLWpvcnAta3M='
function _deobf(s) { return atob(s).split('').reverse().join('') }
const _apiKey = _deobf(_obf)

// state
let _ragCache = null
let _previousResponseId = null
let _mode = 'mock' // 'mock', 'openai', or 'claude'
let _dashboardActions = null

// dashboard actions registry (set by DashboardContent, read by ChatWidget)
export function setDashboardActions(actions) { _dashboardActions = actions }
export function getDashboardActions() { return _dashboardActions }

// dashboard activation (set by Content.js when on MYSITE, called by navigateToDashboard)
let _activateDashboard = null
export function setActivateDashboard(fn) { _activateDashboard = fn }

// navigation registry (set by ChatWidget, provides navigate function from react-router)
let _navigate = null
export function setNavigate(fn) { _navigate = fn }

// navigate to dashboard: goes to MYSITE and activates the dashboard view
export async function navigateToDashboard() {
    const { ROUTES: { MYSITE } } = window.conf
    if (!MYSITE?.path || !_navigate) return false
    _navigate(MYSITE.path)
    // wait for Content.js to mount on MYSITE and register the activation callback
    const ok = await _poll(() => _activateDashboard, 100, 5000)
    if (ok) { _activateDashboard(); return true }
    return false
}

// poll until predicate returns truthy, or timeout
function _poll(predicate, intervalMs, timeoutMs) {
    return new Promise(resolve => {
        let elapsed = 0
        const id = setInterval(() => {
            if (predicate()) { clearInterval(id); resolve(true) }
            elapsed += intervalMs
            if (elapsed >= timeoutMs) { clearInterval(id); resolve(false) }
        }, intervalMs)
    })
}

// german detection type names derived from locales (de.json)
const _typesDe = de.pages.access.vehicle.types
const _subtypesDe = de.pages.access.vehicle.subtypes
const _ALL_DE_TYPE_NAMES = [...new Set([
    ...Object.values(_typesDe),
    ...Object.values(_subtypesDe),
])].sort()

// init: detect mode
export async function init() {
    try {
        const ping = await fetch('/api/chat/ping', { method: 'GET', signal: AbortSignal.timeout(1500) })
        const ct = ping.headers.get('content-type') || ''
        if (ping.ok && ct.includes('application/json')) { _mode = 'claude'; return }
    } catch (e) { /* dev server not running */ }

    if (_apiKey && !_apiKey.startsWith('sk-YOUR')) {
        _mode = 'openai'
    } else {
        _mode = 'mock'
    }
}

export function isMock() { return _mode === 'mock' }
export function getMode() { return _mode }

// load RAG prose files
async function _loadRag() {
    if (_ragCache) return _ragCache
    const files = [
        'system_prompt.txt', 'bautagebuch.txt', 'bauphasen.txt',
        'wetterdaten.txt', 'bilderkennungen_uebersicht.txt',
        'bilderkennungen_taeglich.txt',
    ]
    const parts = []
    for (const f of files) {
        try {
            const resp = await fetch('/static/data/rag/' + f)
            if (resp.ok) {
                const text = await resp.text()
                parts.push('=== ' + f + ' ===\n' + text)
            }
        } catch (e) { /* skip missing */ }
    }
    _ragCache = parts.join('\n\n')
    return _ragCache
}

// build system instructions for the AI
function _buildInstructions(dashboardState) {
    const today = new Date()
    const todayStr = String(today.getDate()).padStart(2, '0') + '.' +
        String(today.getMonth() + 1).padStart(2, '0') + '.' +
        today.getFullYear()

    const parts = []
    parts.push('Heutiges Datum: ' + todayStr)
    parts.push('WICHTIG: Alle Daten enden am 03.02.2026. Bei Anfragen fuer Zeitraeume nach diesem Datum: erklaere die Datenverfuegbarkeit und biete den letzten verfuegbaren Zeitraum an. Setze set_date_range NICHT auf Zeitraeume ohne Daten.')
    parts.push('PFLICHT-AKTIONEN: Wetterfrage → show_weather_streams aufrufen. Erkennungsfrage → switch_tab("erkennungen"). Jede Sachantwort MUSS konkrete Fakten/Zahlen enthalten — NIEMALS nur "Die Visualisierung zeigt jetzt...".')
    parts.push('EINHEITEN: Erkennungszahlen sind Bildzaehlungen (z.B. "48 Bagger-Erkennungen"), NIEMALS Minuten oder Stunden. Erkennungen ≠ individuelle Fahrzeuge.')
    parts.push('VERBOTEN: Keine Kausalitaet herstellen ("wegen Frost nicht gearbeitet"), keine Bewertungen ("laeuft nach Plan"), keine Prognosen, keine Spekulation. Nur Fakten berichten.')
    parts.push('NUR 4 dokumentierte Bauphasen: Erdarbeiten, Gruendung/Fundamente, Rohbau-Stuetzen, Wandmontage. Keine weiteren erfinden.')

    if (dashboardState) {
        const stateLines = ['\nAktueller Dashboard-Zustand:']
        const tab = dashboardState.active_tab || 'erkennungen'
        const tabNames = { detections: 'Erkennungen', weather: 'Wetter' }
        stateLines.push('- Aktiver Tab: ' + (tabNames[tab] || tab))

        const dr = dashboardState.date_range || {}
        if (dr.start) stateLines.push('- Datumsbereich: ' + dr.start + ' bis ' + dr.end)

        const enabled = dashboardState.detection_types_enabled || []
        const available = dashboardState.detection_types_available || []
        if (available.length) {
            if (enabled.length === available.length) {
                stateLines.push('- Erkennungstypen: alle ' + available.length + ' Typen sichtbar')
            } else if (enabled.length) {
                stateLines.push('- Sichtbare Erkennungstypen: ' + enabled.join(', '))
                stateLines.push('- Ausgeblendete Erkennungstypen: ' + available.filter(t => !enabled.includes(t)).join(', '))
            } else {
                stateLines.push('- Erkennungstypen: keine sichtbar')
            }
        }

        const ws = dashboardState.weather_streams || []
        if (ws.length) {
            const streamNames = { temp: 'Temperatur', precipitation: 'Niederschlag', snowfall: 'Schneefall', wind: 'Wind', humidity: 'Luftfeuchtigkeit' }
            stateLines.push('- Wetter-Streams: ' + ws.map(s => streamNames[s] || s).join(', '))
        }

        if (dashboardState.data_loaded !== undefined) {
            stateLines.push(dashboardState.data_loaded
                ? '- Erkennungsdaten: geladen und verfuegbar'
                : '- Erkennungsdaten: NICHT geladen — rufe load_data() auf'
            )
        }

        if (dashboardState.viewer_open && dashboardState.viewer_date) {
            stateLines.push('- Bildbetrachter: GEOEFFNET fuer Datum ' + dashboardState.viewer_date)
        } else {
            stateLines.push('- Bildbetrachter: geschlossen')
        }

        parts.push(stateLines.join('\n'))
    }

    return parts.join('\n\n')
}

// visualization tools (openai function calling definitions)
const VIZ_TOOLS = [
    {
        type: 'function', name: 'show_detection_types',
        description: 'Filtert das Erkennungs-Diagramm und zeigt NUR die angegebenen Objekttypen an. Verfuegbare Typen: ' + _ALL_DE_TYPE_NAMES.join(', '),
        parameters: { type: 'object', properties: { types: { type: 'array', items: { type: 'string' }, description: 'Liste der Objekttypen (deutsche Namen).' } }, required: ['types'], additionalProperties: false },
        strict: true,
    },
    {
        type: 'function', name: 'show_all_detection_types',
        description: 'Zeigt alle Objekttypen im Erkennungs-Diagramm an.',
        parameters: { type: 'object', properties: {}, required: [], additionalProperties: false },
        strict: true,
    },
    {
        type: 'function', name: 'set_date_range',
        description: 'Setzt den angezeigten Datumsbereich fuer alle Tabs. ACHTUNG: Daten sind nur von 05.11.2025 bis 03.02.2026 verfuegbar. Setze keine Daten ausserhalb dieses Bereichs.',
        parameters: { type: 'object', properties: { start: { type: 'string', description: 'Startdatum TT.MM.JJ oder TT.MM.JJJJ' }, end: { type: 'string', description: 'Enddatum TT.MM.JJ oder TT.MM.JJJJ' } }, required: ['start', 'end'], additionalProperties: false },
        strict: true,
    },
    {
        type: 'function', name: 'switch_tab',
        description: 'Wechselt den aktiven Tab.',
        parameters: { type: 'object', properties: { tab: { type: 'string', enum: ['erkennungen', 'wetter'], description: 'Der Ziel-Tab.' } }, required: ['tab'], additionalProperties: false },
        strict: true,
    },
    {
        type: 'function', name: 'show_weather_streams',
        description: 'Zeigt NUR die angegebenen Wetter-Streams an und wechselt zum Wetter-Tab.',
        parameters: { type: 'object', properties: { streams: { type: 'array', items: { type: 'string', enum: ['temp', 'precipitation', 'snowfall', 'wind', 'humidity'] } } }, required: ['streams'], additionalProperties: false },
        strict: true,
    },
    {
        type: 'function', name: 'reset_view',
        description: 'Setzt die Visualisierung auf die Standardansicht zurueck.',
        parameters: { type: 'object', properties: {}, required: [], additionalProperties: false },
        strict: true,
    },
    {
        type: 'function', name: 'load_data',
        description: 'Laedt die Erkennungsdaten fuer den aktuellen Datumsbereich.',
        parameters: { type: 'object', properties: {}, required: [], additionalProperties: false },
        strict: true,
    },
    {
        type: 'function', name: 'open_image_viewer',
        description: 'Oeffnet den Bildbetrachter fuer ein bestimmtes Datum und/oder einen Erkennungstyp. Verwende diese Funktion wenn der Nutzer ein Bild sehen moechte.',
        parameters: { type: 'object', properties: { date: { type: 'string', description: 'Datum im Format TT.MM.JJ oder TT.MM.JJJJ. Leerer String wenn kein bestimmtes Datum gewuenscht.' }, detection_type: { type: 'string', description: 'Deutscher Erkennungstyp zum Filtern (z.B. Bagger, LKW). Leerer String wenn kein Typfilter gewuenscht.' } }, required: ['date', 'detection_type'], additionalProperties: false },
        strict: true,
    },
]

const FUNC_TO_ACTION = {
    show_detection_types: 'show_only_detections',
    show_all_detection_types: 'show_all_detections',
    set_date_range: 'set_dates',
    switch_tab: 'switch_tab',
    show_weather_streams: 'show_weather_streams',
    reset_view: 'reset_view',
    load_data: 'load_data',
    open_image_viewer: 'open_viewer',
}

const TAB_MAP = { erkennungen: 'detections', wetter: 'weather' }

// map function call to dashboard action
function _functionCallToAction(funcName, args) {
    const actionType = FUNC_TO_ACTION[funcName]
    if (!actionType) return [null, 'Unknown function: ' + funcName]

    if (typeof args === 'string') args = JSON.parse(args)
    const action = { action: actionType }
    let result = ''

    if (funcName === 'show_detection_types') {
        action.types = args.types || []
        result = 'Done: showing only ' + JSON.stringify(action.types)
    } else if (funcName === 'set_date_range') {
        action.start = args.start || ''
        action.end = args.end || ''
        result = 'Done: date range set to ' + action.start + ' - ' + action.end
    } else if (funcName === 'switch_tab') {
        const rawTab = args.tab || 'erkennungen'
        action.tab = TAB_MAP[rawTab] || rawTab
        result = 'Done: switched to tab ' + action.tab
    } else if (funcName === 'show_weather_streams') {
        action.streams = args.streams || []
        result = 'Done: weather streams set to ' + JSON.stringify(action.streams)
    } else if (funcName === 'open_image_viewer') {
        const dateArg = args.date || ''
        const typeArg = args.detection_type || ''
        let viewerDate = null
        if (dateArg) {
            try {
                const parsed = dashboardParseDate(dateArg)
                viewerDate = dashboardFmtDate(parsed)
            } catch (e) { viewerDate = null }
        }
        if (!viewerDate) {
            // fallback: find best day for the requested type from summary data
            if (typeArg) viewerDate = _findBestDayForType(typeArg)
        }
        if (!viewerDate) {
            // last resort: use last available detection date
            const { summaryData } = getDashboardRawData()
            if (summaryData?.length) {
                const last = summaryData[summaryData.length - 1]
                if (last.timestamp) viewerDate = dashboardFmtDate(new Date(last.timestamp))
            }
        }
        action.date = viewerDate
        if (typeArg) action.types = [typeArg]
        result = viewerDate ? 'Done: image viewer opened for ' + viewerDate + (typeArg ? ' filtered to ' + typeArg : '') : 'Error: no images found'
    } else {
        result = 'Done: ' + actionType
    }

    return [action, result]
}

// find best day for a detection type (reverse-lookup german name -> column key via locales)
function _findBestDayForType(deTypeName) {
    const { summaryData } = getDashboardRawData()
    if (!summaryData?.length) return null

    // reverse-lookup: german name -> column key
    let colKey = null
    for (const [k, v] of Object.entries(_typesDe)) { if (v === deTypeName) { colKey = k; break } }
    if (!colKey) {
        for (const [k, v] of Object.entries(_subtypesDe)) { if (v === deTypeName) { colKey = k; break } }
    }
    if (!colKey) return null

    // aggregate per day, find the day with the highest count
    const dayCounts = {}
    for (const row of summaryData) {
        const val = row[colKey] || 0
        if (val <= 0) continue
        const date = new Date(row.timestamp)
        const dayKey = dashboardFmtDate(date)
        dayCounts[dayKey] = (dayCounts[dayKey] || 0) + val
    }

    let bestDay = null, bestCount = 0
    for (const [day, count] of Object.entries(dayCounts)) {
        if (count > bestCount) { bestDay = day; bestCount = count }
    }
    return bestDay
}

// post-processing: remove confabulations, tech leaks, causal language
function _postprocessReply(text) {

    // remove tech internal sentences
    const techKeywords = ['hochgeladen', 'hochgeladene', 'lade die datei', 'dateien', 'dokumente', 'file_search', 'vektordatenbank', 'csv', 'fahrzeugaktivitaet', 'fahrzeugaktivität']
    let sentences = text.split(/(?<=[.!?])\s+/)
    sentences = sentences.filter(s => !techKeywords.some(kw => s.toLowerCase().includes(kw)))
    text = sentences.join(' ').trim()

    // remove causal language about inactivity + weather
    const causalKeywords = ['aufgrund', 'führte zu', 'fuehrte zu', 'zusammenhang', 'beeinflusst', 'korrelieren', 'korreliert', 'verbunden mit', 'zurueckzufuehren', 'zurückzuführen', 'bedingt durch', 'infolge von', 'infolge des', 'wegen des wetter', 'wegen schlechtem', 'wegen schnee', 'wegen frost', 'wegen regen', 'verlangsam', 'ermoeglicht', 'ermöglicht', 'beguenstigt', 'begünstigt', 'erschwert', 'verhindert', 'begrenzt', 'beeintraechtigt', 'beeinträchtigt', 'trotz des', 'trotz der', 'trotz dem', 'trotz frost', 'trotz schnee', 'trotz regen', 'trotz kaelte', 'trotz kälte', 'trotzdem gearbeitet', 'trotzdem gebaut', 'obwohl es', 'obwohl das wetter', 'obwohl frost', 'obwohl schnee', 'dennoch gearbeitet', 'dennoch gebaut', 'spiegelt den', 'spiegelt das', 'spiegelt die', 'zeigt den uebergang', 'zeigt den übergang', 'deutet auf', 'deutet darauf', 'laesst darauf schliessen', 'lässt darauf schließen', 'weist auf einen', 'weist auf eine']
    const causalBauWords = ['bauaktiv', 'bau ', 'gearbeitet', 'aktivit', 'inaktiv', 'ruht', 'eingestellt', 'kein bau', 'fortschritt', 'wandel', 'wechsel', 'geraete-mix', 'geräte-mix', 'uebergang', 'übergang']
    const denialWords = ['nicht herstellen', 'kann ich aber nicht', 'kann ich nicht herstellen', 'keinen zusammenhang', 'kein kausaler', 'keinen kausalen']

    sentences = text.split(/(?<=[.!?])\s+/)
    sentences = sentences.filter(s => {
        const sLower = s.toLowerCase()
        if (causalKeywords.some(kw => sLower.includes(kw))) {
            const isCausal = causalBauWords.some(w => sLower.includes(w))
            if (isCausal && !denialWords.some(d => sLower.includes(d))) return false
        }
        return true
    })
    text = sentences.join(' ').trim()

    // remove evaluative statements
    const evalPhrases = ['läuft nach plan', 'laeuft nach plan', 'laeuft gut', 'sieht gut aus', 'geht gut voran', 'guter fortschritt', 'zufriedenstellend', 'im zeitplan', 'planmässig', 'planmaessig', 'alles in ordnung', 'gut aufgestellt', 'alles im griff', 'positiv zu bewerten', 'auf gutem weg']
    sentences = text.split(/(?<=[.!?])\s+/)
    sentences = sentences.filter(s => !evalPhrases.some(ep => s.toLowerCase().includes(ep)))
    text = sentences.join(' ').trim()

    // replace invalid type names
    const typeReplacements = { Kleinfahrzeug: 'Transporter', Kleinfahrzeuge: 'Transporter', Strassenwalze: 'Walze', 'Straßenwalze': 'Walze', Turmdrehkran: 'Kran', Teleskoplader: 'Teleskopbuehne', Personen: 'Person' }
    for (const [wrong, right] of Object.entries(typeReplacements)) {
        if (text.includes(wrong)) text = text.split(wrong).join(right)
    }

    // clean whitespace
    text = text.replace(/\s{2,}/g, ' ').trim()
    text = text.replace(/\n\s*\n\s*\n/g, '\n\n')

    return text
}

// mock mode responses and command parsing
const MOCK_RESPONSES = [
    'Das BREMER Logistikhallen-Projekt begann am 5. November 2025. Der Rohbau ist seit etwa Ende Januar 2026 weitgehend fertiggestellt.',
    'Aktuell sind auf der Baustelle diverse Geraete im Einsatz, darunter ein Sennebogen Mobilkran, Hitachi Bagger, Radlader und Teleskopstapler.',
    'Die Fertigteilmontage der Rueckwand wurde in nur 4 Arbeitstagen Mitte Dezember 2025 abgeschlossen.',
    'Ich bin der Baustellenassistent fuer das BREMER Famila-Projekt. Ich kann Ihnen Informationen zum Baufortschritt geben.',
    'Die Bauarbeiten liefen trotz winterlicher Bedingungen durchgehend weiter.',
]
let _mockCounter = 0

function _parseMockCommands(message, availableTypes) {
    const msg = message.trim().toLowerCase()
    const typeLookup = {}
    for (const t of (availableTypes || [])) typeLookup[t.toLowerCase()] = t

    function matchType(fragment) {
        fragment = fragment.trim().toLowerCase()
        for (const [lc, orig] of Object.entries(typeLookup)) {
            if (lc.includes(fragment) || fragment.includes(lc)) return orig
        }
        return null
    }

    function matchMultipleTypes(fragment) {
        return fragment.split(',').map(p => matchType(p.trim())).filter(Boolean)
    }

    if (/^(zeige\s+alle|alle\s+anzeigen|show\s+all)$/.test(msg))
        return ['Alle Objekttypen werden angezeigt.', [{ action: 'show_all_detections' }]]

    let m = msg.match(/^(zeige\s+nur|show\s+only)\s+(.+)$/)
    if (m) {
        const matched = matchMultipleTypes(m[2])
        if (matched.length) return ['Nur ' + matched.join(', ') + ' wird angezeigt.', [{ action: 'show_only_detections', types: matched }]]
    }

    m = msg.match(/^(datum|set\s+dates?)\s+(\S+)\s+(bis|to)\s+(\S+)$/)
    if (m) return ['Datumsbereich auf ' + m[2] + ' bis ' + m[4] + ' gesetzt.', [{ action: 'set_dates', start: m[2], end: m[4] }]]

    const tabMap = { erkennungen: 'detections', wetter: 'weather' }
    m = msg.match(/^tab\s+(\S+)$/)
    if (m) return ['Tab gewechselt.', [{ action: 'switch_tab', tab: tabMap[m[1]] || m[1] }]]

    if (/^(reset|zuruecksetzen|standardansicht)$/.test(msg))
        return ['Standardansicht wiederhergestellt.', [{ action: 'reset_view' }]]

    return [null, null]
}

// openai API call
async function _callOpenAI(payload) {
    const resp = await fetch('https://api.openai.com/v1/responses', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + _apiKey,
        },
        body: JSON.stringify(payload),
    })
    if (!resp.ok) {
        const errBody = await resp.text()
        throw new Error('OpenAI ' + resp.status + ': ' + errBody.slice(0, 200))
    }
    return resp.json()
}

// extract text content from openai response
function _extractText(resp) {
    let text = ''
    for (const item of (resp.output || [])) {
        if (item.type === 'message') {
            for (const c of (item.content || [])) {
                if (c.type === 'output_text') text += c.text
            }
        }
    }
    if (!text && resp.output_text) text = resp.output_text
    return text
}

// send message (main entry point)
export async function sendMessage(userMessage, dashboardState) {

    // claude dev server mode
    if (_mode === 'claude') {
        try {
            const resp = await fetch('/api/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: userMessage, dashboard_state: dashboardState }),
            })
            const json = await resp.json()
            if (json.reply) json.reply = _postprocessReply(json.reply)
            return json
        } catch (e) {
            return { reply: 'Claude-Fehler: ' + String(e).slice(0, 200), mock: false }
        }
    }

    // mock mode
    if (_mode === 'mock') {
        const available = dashboardState ? (dashboardState.detection_types_available || []) : []
        const [cmdReply, cmdActions] = _parseMockCommands(userMessage, available)
        if (cmdReply !== null) return { reply: cmdReply, actions: cmdActions, mock: true }

        const reply = MOCK_RESPONSES[_mockCounter % MOCK_RESPONSES.length]
        _mockCounter++
        return { reply, mock: true }
    }

    // openai mode
    try {
        const rag = await _loadRag()
        const instructions = rag + '\n\n' + _buildInstructions(dashboardState)

        const payload = {
            model: 'gpt-4o',
            instructions,
            input: [{ role: 'user', content: userMessage }],
            tools: VIZ_TOOLS,
        }
        if (_previousResponseId) payload.previous_response_id = _previousResponseId

        let response
        try {
            response = await _callOpenAI(payload)
        } catch (initErr) {
            // stale session retry
            if (String(initErr).toLowerCase().includes('tool output') && _previousResponseId) {
                _previousResponseId = null
                delete payload.previous_response_id
                response = await _callOpenAI(payload)
            } else {
                throw initErr
            }
        }

        // multi-round function calling loop
        const actions = []
        let earlyText = ''
        for (let round = 0; round < 5; round++) {
            const roundText = _extractText(response)
            if (roundText) earlyText = roundText
            const fcOutputs = []
            for (const item of (response.output || [])) {
                if (item.type === 'function_call') {
                    const [action, resultStr] = _functionCallToAction(item.name, item.arguments)
                    if (action) actions.push(action)
                    fcOutputs.push({ type: 'function_call_output', call_id: item.call_id, output: resultStr })
                }
            }
            if (fcOutputs.length === 0) break

            const contPayload = {
                model: 'gpt-4o',
                instructions,
                input: fcOutputs,
                previous_response_id: response.id,
                tools: VIZ_TOOLS,
            }
            response = await _callOpenAI(contPayload)
        }

        _previousResponseId = response.id

        // extract text reply
        let replyText = _extractText(response) || earlyText

        // follow-up: weak reply detection
        const VIZ_ONLY_MARKERS = ['visualisierung', 'grafik zeigt', 'grafik angepasst', 'grafik aktualisiert', 'daten bereitgestellt', 'daten angezeigt', 'ansehen', 'details brauchst', 'wissen moechtest', 'wobei ich helfen', 'soll ich']
        const OOS_MARKERS_EARLY = ['liegen mir keine informationen', 'keine daten', 'nicht verfuegbar', 'ausserhalb meiner', 'keine prognose', 'keine aussage', 'kann ich nicht liefern', 'kann ich keine aussage', 'kann ich nicht beurteilen', 'kann ich nicht sagen', 'kann ich nicht ermitteln']

        if (replyText) {
            const rLower = replyText.toLowerCase()
            const isEarlyOos = OOS_MARKERS_EARLY.some(m => rLower.includes(m))

            let isVizOnly = replyText.length < 350 && VIZ_ONLY_MARKERS.some(m => rLower.includes(m))
            let isTooShort = replyText.trim().length < 120
            let isDeflection = replyText.length < 200 && (rLower.includes('möchtest du') || rLower.includes('soll ich') || rLower.includes('wonach du suchst'))

            if (isTooShort && actions.length) isTooShort = false
            if (isVizOnly && actions.length) isVizOnly = false
            if (isEarlyOos) { isVizOnly = false; isTooShort = false; isDeflection = false }

            if (isVizOnly || isTooShort || isDeflection) {
                try {
                    const fuPayload = {
                        model: 'gpt-4o', instructions,
                        input: [{ role: 'user', content: 'Deine vorherige Antwort beantwortet die Frage nicht mit Fakten. Bitte beantworte die urspruengliche Frage jetzt mit konkreten Fakten und Zahlen aus den Daten. Nenne die konkreten Zahlen, Daten und Fahrzeugtypen. Wiederhole NICHT den Hinweis auf die Visualisierung.' }],
                        previous_response_id: response.id, tools: VIZ_TOOLS,
                    }
                    const fuResp = await _callOpenAI(fuPayload)
                    const fuText = _extractText(fuResp)
                    if (fuText && fuText.length > 80) {
                        replyText = fuText
                        _previousResponseId = fuResp.id
                    }
                } catch (e) { /* ignore */ }
            }

            // follow-up: no viz actions
            const OOS_MARKERS = [...OOS_MARKERS_EARLY, 'keine angaben', 'keine kostendaten', 'drei tabs', 'kann ich nicht treffen', 'keine subunternehmer']
            const isOos = OOS_MARKERS.some(m => replyText.toLowerCase().includes(m))
            const isPureOos = isOos && replyText.length < 400

            if (!actions.length && replyText.length > 100 && !isPureOos) {
                try {
                    const vizPayload = {
                        model: 'gpt-4o',
                        input: [{ role: 'user', content: 'Gut, danke fuer die Fakten. Bitte passe jetzt auch die Visualisierung passend zu deiner Antwort an. Antworte NUR mit den Funktionsaufrufen.' }],
                        previous_response_id: response.id, tools: VIZ_TOOLS,
                    }
                    const vizResp = await _callOpenAI(vizPayload)
                    const fcOutputs = []
                    for (const item of (vizResp.output || [])) {
                        if (item.type === 'function_call') {
                            const [action, resultStr] = _functionCallToAction(item.name, item.arguments)
                            if (action) actions.push(action)
                            fcOutputs.push({ type: 'function_call_output', call_id: item.call_id, output: resultStr })
                        }
                    }
                    if (fcOutputs.length) {
                        const finalResp = await _callOpenAI({
                            model: 'gpt-4o',
                            input: fcOutputs, previous_response_id: vizResp.id, tools: VIZ_TOOLS,
                        })
                        _previousResponseId = finalResp.id
                    } else {
                        _previousResponseId = vizResp.id
                    }
                } catch (e) { /* ignore */ }
            }

            // confabulation check (Minuten/Stunden)
            const hasTimeConfab = /\d+\s*(?:Stunden?|Minuten?)\b(?!\s*(?:sp.ter|fr.her|lang|pro Tag|Verz.g))/.test(replyText)
            const weatherTime = /(?:Wind|Temperatur|Niederschlag|Schnee).{0,40}\d+\s*(?:Stunden?|Minuten?)/.test(replyText)
            if (hasTimeConfab && !weatherTime) {
                try {
                    const corrPayload = {
                        model: 'gpt-4o', instructions,
                        input: [{ role: 'user', content: 'WICHTIG: Deine vorherige Antwort enthaelt Zeitangaben (Minuten, Stunden) fuer Fahrzeugerkennungen. Das ist FALSCH. Die Daten enthalten KEINE Zeitangaben — nur Bildzaehlungen. Bitte beantworte die Frage erneut und verwende NUR die Einheit Erkennungen.' }],
                        previous_response_id: response.id, tools: VIZ_TOOLS,
                    }
                    const corrResp = await _callOpenAI(corrPayload)
                    const corrText = _extractText(corrResp)
                    if (corrText && corrText.length > 80) {
                        replyText = corrText
                        _previousResponseId = corrResp.id
                    }
                } catch (e) { /* ignore */ }
            }
        }

        replyText = _postprocessReply(replyText || '')

        const result = { reply: replyText, mock: false }
        if (actions.length) result.actions = actions
        return result

    } catch (e) {
        return { reply: 'API-Fehler: ' + String(e).slice(0, 200) + '. Bitte versuchen Sie es spaeter erneut.', mock: false }
    }
}

// reset conversation
export function resetConversation() {
    _previousResponseId = null
    _ragCache = null
}